import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Mountain, Users, Calendar, FolderOpen, 
  BookOpen, Images, Bed, MessageSquare, Heart, Settings,
  LogOut, Menu, X, Search, Plus, Edit, Trash2, Upload, Save,
  Phone, Mail, MapPin, Globe
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { LANGUAGES } from '../../contexts/LanguageContext';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIDEBAR_ITEMS = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
  { icon: Mountain, label: 'Falesie', path: '/admin/crags' },
  { icon: Users, label: 'Team', path: '/admin/team' },
  { icon: Calendar, label: 'Eventi', path: '/admin/events' },
  { icon: FolderOpen, label: 'Progetti', path: '/admin/projects' },
  { icon: BookOpen, label: 'Blog', path: '/admin/blog' },
  { icon: Images, label: 'Gallery', path: '/admin/gallery' },
  { icon: Bed, label: 'Alloggi', path: '/admin/accommodations' },
  { icon: MessageSquare, label: 'Messaggi', path: '/admin/messages' },
  { icon: Heart, label: 'Donazioni', path: '/admin/donations' },
  { icon: Search, label: 'SEO', path: '/admin/seo' },
  { icon: Upload, label: 'Uploads', path: '/admin/uploads' },
  { icon: Settings, label: 'Impostazioni', path: '/admin/settings' },
];

function AdminSidebar({ currentPath, sidebarOpen, setSidebarOpen, logout }) {
  return (
    <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#0A0A0A] transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
      <div className="flex items-center justify-between h-16 px-6 border-b border-gray-800">
        <Link to="/" className="text-white font-heading text-lg uppercase">CMS Admin</Link>
        <button className="lg:hidden text-white" onClick={() => setSidebarOpen(false)}><X className="w-6 h-6" /></button>
      </div>
      <nav className="p-4 space-y-1 max-h-[calc(100vh-8rem)] overflow-y-auto">
        {SIDEBAR_ITEMS.map((item) => (
          <Link key={item.path} to={item.path} className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${currentPath === item.path ? 'bg-[#FF6D00] text-white' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
            <item.icon className="w-5 h-5" /><span className="text-sm">{item.label}</span>
          </Link>
        ))}
      </nav>
      <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-800">
        <button onClick={logout} className="flex items-center gap-3 px-4 py-3 text-gray-400 hover:text-white w-full">
          <LogOut className="w-5 h-5" /><span className="text-sm">Logout</span>
        </button>
      </div>
    </aside>
  );
}

function TranslatedInput({ label, value, onChange, placeholder, multiline = false }) {
  const [activeLang, setActiveLang] = useState('it');
  const Component = multiline ? Textarea : Input;
  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium">{label}</label>
      <Tabs value={activeLang} onValueChange={setActiveLang}>
        <TabsList className="h-8">
          {Object.values(LANGUAGES).map(lang => (<TabsTrigger key={lang.code} value={lang.code} className="text-xs px-2 py-1">{lang.flag}</TabsTrigger>))}
        </TabsList>
        {Object.values(LANGUAGES).map(lang => (
          <TabsContent key={lang.code} value={lang.code} className="mt-2">
            <Component value={value?.[lang.code] || ''} onChange={(e) => onChange({ ...value, [lang.code]: e.target.value })} placeholder={`${placeholder} (${lang.name})`} rows={multiline ? 4 : undefined} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

export function AdminSettings() {
  const { logout, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('contact');
  
  // Contact settings
  const [contactSettings, setContactSettings] = useState({
    address: '',
    phone: '',
    phone_name: '',
    email: '',
    gps_lat: 0,
    gps_lng: 0
  });
  
  // Site settings
  const [siteSettings, setSiteSettings] = useState({
    site_name: { it: 'Caltavuturo Climbing', en: 'Caltavuturo Climbing', fr: 'Caltavuturo Climbing', de: 'Caltavuturo Climbing', es: 'Caltavuturo Climbing' },
    site_description: { it: '', en: '', fr: '', de: '', es: '' },
    footer_text: { it: '', en: '', fr: '', de: '', es: '' },
    social_facebook: '',
    social_instagram: '',
    social_youtube: ''
  });

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/admin/login'); return; }
    fetchSettings();
  }, [isAuthenticated, authLoading, navigate]);

  const fetchSettings = async () => {
    try {
      const [contactRes, siteRes] = await Promise.all([
        axios.get(`${API}/contact/settings`),
        axios.get(`${API}/settings/site`)
      ]);
      
      if (contactRes.data) {
        setContactSettings(contactRes.data);
      }
      if (siteRes.data && Object.keys(siteRes.data).length > 0) {
        setSiteSettings(prev => ({ ...prev, ...siteRes.data }));
      }
    } catch (error) { 
      console.error('Error fetching settings:', error);
    }
    finally { setLoading(false); }
  };

  const saveContactSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/contact/settings`, contactSettings);
      toast.success('Impostazioni contatto salvate');
    } catch (error) { 
      toast.error('Errore nel salvataggio'); 
    }
    finally { setSaving(false); }
  };

  const saveSiteSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/site`, siteSettings);
      toast.success('Impostazioni sito salvate');
    } catch (error) { 
      toast.error('Errore nel salvataggio'); 
    }
    finally { setSaving(false); }
  };

  if (authLoading || !isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-settings">
      <AdminSidebar currentPath="/admin/settings" sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} logout={logout} />
      <div className="lg:ml-64">
        <header className="bg-white border-b h-16 flex items-center justify-between px-6">
          <button className="lg:hidden" onClick={() => setSidebarOpen(true)}><Menu className="w-6 h-6" /></button>
          <h1 className="font-heading text-xl uppercase">Impostazioni</h1>
        </header>

        <main className="p-6">
          {loading ? (
            <div className="flex justify-center py-20"><div className="animate-spin w-8 h-8 border-4 border-[#FF6D00] border-t-transparent rounded-full" /></div>
          ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-6">
                <TabsTrigger value="contact">Contatti</TabsTrigger>
                <TabsTrigger value="site">Sito</TabsTrigger>
                <TabsTrigger value="social">Social</TabsTrigger>
              </TabsList>

              {/* Contact Settings */}
              <TabsContent value="contact">
                <div className="bg-white rounded-lg border p-6">
                  <h2 className="font-heading text-lg uppercase mb-6">Informazioni di Contatto</h2>
                  
                  <div className="space-y-4 max-w-2xl">
                    <div>
                      <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                        <MapPin className="w-4 h-4" /> Indirizzo
                      </label>
                      <Input 
                        value={contactSettings.address} 
                        onChange={(e) => setContactSettings({...contactSettings, address: e.target.value})} 
                        placeholder="Via Roma 1, 90022 Caltavuturo (PA)"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                          <Phone className="w-4 h-4" /> Telefono
                        </label>
                        <Input 
                          value={contactSettings.phone} 
                          onChange={(e) => setContactSettings({...contactSettings, phone: e.target.value})} 
                          placeholder="+39 123 456 7890"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Referente</label>
                        <Input 
                          value={contactSettings.phone_name} 
                          onChange={(e) => setContactSettings({...contactSettings, phone_name: e.target.value})} 
                          placeholder="Nome referente"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                        <Mail className="w-4 h-4" /> Email
                      </label>
                      <Input 
                        type="email"
                        value={contactSettings.email} 
                        onChange={(e) => setContactSettings({...contactSettings, email: e.target.value})} 
                        placeholder="info@caltavuturoclimbing.it"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">GPS Latitudine</label>
                        <Input 
                          type="number"
                          step="0.0001"
                          value={contactSettings.gps_lat} 
                          onChange={(e) => setContactSettings({...contactSettings, gps_lat: parseFloat(e.target.value) || 0})} 
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">GPS Longitudine</label>
                        <Input 
                          type="number"
                          step="0.0001"
                          value={contactSettings.gps_lng} 
                          onChange={(e) => setContactSettings({...contactSettings, gps_lng: parseFloat(e.target.value) || 0})} 
                        />
                      </div>
                    </div>
                    
                    <div className="pt-4">
                      <Button onClick={saveContactSettings} disabled={saving} className="btn-primary">
                        <Save className="w-4 h-4 mr-2" />
                        {saving ? 'Salvataggio...' : 'Salva Contatti'}
                      </Button>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Site Settings */}
              <TabsContent value="site">
                <div className="bg-white rounded-lg border p-6">
                  <h2 className="font-heading text-lg uppercase mb-6">Impostazioni Sito</h2>
                  
                  <div className="space-y-4 max-w-2xl">
                    <TranslatedInput 
                      label="Nome Sito" 
                      value={siteSettings.site_name} 
                      onChange={(v) => setSiteSettings({...siteSettings, site_name: v})} 
                      placeholder="Caltavuturo Climbing"
                    />
                    
                    <TranslatedInput 
                      label="Descrizione Sito" 
                      value={siteSettings.site_description} 
                      onChange={(v) => setSiteSettings({...siteSettings, site_description: v})} 
                      placeholder="Descrizione breve del sito"
                      multiline
                    />
                    
                    <TranslatedInput 
                      label="Testo Footer" 
                      value={siteSettings.footer_text} 
                      onChange={(v) => setSiteSettings({...siteSettings, footer_text: v})} 
                      placeholder="Testo da mostrare nel footer"
                      multiline
                    />
                    
                    <div className="pt-4">
                      <Button onClick={saveSiteSettings} disabled={saving} className="btn-primary">
                        <Save className="w-4 h-4 mr-2" />
                        {saving ? 'Salvataggio...' : 'Salva Impostazioni Sito'}
                      </Button>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Social Settings */}
              <TabsContent value="social">
                <div className="bg-white rounded-lg border p-6">
                  <h2 className="font-heading text-lg uppercase mb-6">Link Social</h2>
                  
                  <div className="space-y-4 max-w-2xl">
                    <div>
                      <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                        <Globe className="w-4 h-4" /> Facebook
                      </label>
                      <Input 
                        value={siteSettings.social_facebook} 
                        onChange={(e) => setSiteSettings({...siteSettings, social_facebook: e.target.value})} 
                        placeholder="https://facebook.com/..."
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                        <Globe className="w-4 h-4" /> Instagram
                      </label>
                      <Input 
                        value={siteSettings.social_instagram} 
                        onChange={(e) => setSiteSettings({...siteSettings, social_instagram: e.target.value})} 
                        placeholder="https://instagram.com/..."
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                        <Globe className="w-4 h-4" /> YouTube
                      </label>
                      <Input 
                        value={siteSettings.social_youtube} 
                        onChange={(e) => setSiteSettings({...siteSettings, social_youtube: e.target.value})} 
                        placeholder="https://youtube.com/..."
                      />
                    </div>
                    
                    <div className="pt-4">
                      <Button onClick={saveSiteSettings} disabled={saving} className="btn-primary">
                        <Save className="w-4 h-4 mr-2" />
                        {saving ? 'Salvataggio...' : 'Salva Social'}
                      </Button>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          )}
        </main>
      </div>
    </div>
  );
}
